import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import Interface from '../features/onboarding/pages/LoginForm.js'
import ResetPassword from '../features/onboarding/pages/ResetPassword.js'
import Dashboard from '../features/dashboard/pages/Dashboard.js'
import { TeamProvider, useTeam } from "../shared/context/teamContext";
import { SubscriptionProvider } from "../shared/context/subscriptionContext";
import { PreferencesProvider } from "../shared/context/preferencesContext";
import CustomDashboard from "../features/dashboard/pages/CustomDashboard.js";
import CustomDashboardWrapper from "../features/dashboard/pages/CustomDashboardWrapper.js";
import OverviewWrapper from "../features/dashboard/pages/OverviewWrapper.js";
import CustomEvents from "../features/dashboard/pages/CustomEvents.js";
import PublicDashboardPage from "../features/dashboard/pages/PublicDashboardPage.js";
import AnalyticsLayout from "../features/analytics/mixpanel/pages/AnalyticsLayout.js";
import BoardViewPage from "../features/analytics/mixpanel/pages/BoardViewPage.js";
import PublicBoardPage from "../features/analytics/mixpanel/pages/PublicBoardPage.js";
import InsightsEditorPage from "../features/analytics/mixpanel/pages/InsightsEditorPage.js";
import FlowEditorPage from "../features/analytics/mixpanel/pages/FlowEditorPage.js";
import ReportsPage from "../features/analytics/mixpanel/pages/ReportsPage.js";
import SubscriptionRequired from "../shared/components/subscription/SubscriptionRequired";
import SubscriptionProtectedRoute from "../shared/components/subscription/SubscriptionProtectedRoute";
import TeamOnboarding from '../features/onboarding/pages/TeamOnboarding.js';
import ErrorBoundary from '../shared/components/ErrorBoundary';
import { AnalyticsCacheProvider } from '../shared/context/AnalyticsCacheContext.js';
import '../shared/styles/animations.css';
import '../shared/styles/resizeObserverFix.css';
import suppressResizeObserverErrors from '../shared/utils/resizeObserverFix';

// Apply ResizeObserver error suppression globally
suppressResizeObserverErrors();

// Title updater component - this doesn't need access to Team context
const TitleUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean); // removes empty strings
    const lastSegment = pathSegments[pathSegments.length - 1] || 'login';

    const titleMap = {
      dashboard: 'Home',
      analytics: 'Home',
      reports: 'Home',
      boards: 'Reports',
      editor: 'Report Editor',
      settings: 'Settings',
      billing: 'Billing',
      teamsSection: 'Teams',
      offchain: 'Offchain Analytics',
      'custom-dashboard': 'Custom Dashboard',
      'query-studio': 'Query Studio',
      campaigns: 'Campaigns',
      activity: 'Activity',
      'custom-events': 'Custom Events',
      history: 'History',
      managewebsites: 'Manage Websites',
      'flow': 'Feature Flow',
      'cq-intelligence': 'CQ Intelligence',
      cipher: 'Cipher',
      login: 'Login',
      signup: 'Sign Up',
      'reset-password': 'Reset Password',
    };

    const pageTitle = titleMap[lastSegment.toLowerCase()] || 'MyApp';
    document.title = `${pageTitle}`;
  }, [location]);

  return null;
};

// Favicon updater component - switches favicon based on dark/light mode
const FaviconUpdater = () => {
  useEffect(() => {
    const updateFavicon = () => {
      // Check if user prefers dark mode
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      // Find existing favicon link or create a new one
      let faviconLink = document.querySelector("link[rel~='icon']");
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      }
      
      // Get the public URL (for React apps, this is usually empty string or process.env.PUBLIC_URL)
      const publicUrl = process.env.PUBLIC_URL || '';
      
      // Set the appropriate favicon based on theme
      // Supports both .ico and .png formats - try .ico first, fallback to .png
      const faviconName = prefersDark ? 'favicon-dark' : 'favicon-light';
      faviconLink.href = `${publicUrl}/${faviconName}.ico`;
    };

    // Initial update
    updateFavicon();

    // Listen for theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => updateFavicon();
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Fallback for older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  return null;
};

const OnboardingRedirector = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedTeam, isLoading } = useTeam();

  useEffect(() => {
    if (isLoading) return;
    if (!selectedTeam || !selectedTeam.name) return;

    const path = location.pathname;
    if (path.startsWith('/login') || path.startsWith('/signup') || path.startsWith('/reset-password')) {
      return;
    }

    const pathSegments = path.split('/').filter(Boolean);
    const pathTeam = pathSegments.length > 0 ? decodeURIComponent(pathSegments[0]) : null;
    const teamSlug = selectedTeam.name || selectedTeam._id || '';
    const encodedSlug = encodeURIComponent(teamSlug);

    const requiresOnboarding = !!selectedTeam.isOwner && !(selectedTeam?.onboarding?.isComplete);
    const isOnboardingRoute = pathSegments.length >= 2 && pathSegments[1] === 'onboarding';

    if (requiresOnboarding && (!isOnboardingRoute || (pathTeam && pathTeam !== teamSlug))) {
      navigate(`/${encodedSlug}/onboarding`, { replace: true });
      return;
    }

    if (!requiresOnboarding && isOnboardingRoute) {
      navigate(`/${encodedSlug}/overview`, { replace: true });
    }
  }, [selectedTeam, isLoading, location.pathname, navigate]);

  return null;
};

const LegacyEventsRedirect = () => {
  const { team } = useParams();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/${team}/activity`} replace />;
};

const OffchainRedirect = () => {
  const { team } = useParams();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/${team}/overview?primary=offchain`} replace />;
};

// Redirect /dashboard to Home (Reports page) for the selected team
const DashboardToHomeRedirect = () => {
  const { selectedTeam, isLoading } = useTeam();
  if (isLoading) return null;
  const teamSlug = selectedTeam?.name || selectedTeam?._id || localStorage.getItem('selectedTeam');
  if (!teamSlug) {
    return <Navigate to="/login" replace />;
  }
  const encoded = encodeURIComponent(teamSlug);
  return <Navigate to={`/${encoded}/analytics/reports`} replace />;
};

// OnchainRedirect removed - onchain functionality removed

// Team data refresher is no longer needed - teamContext handles caching and API calls efficiently

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <TeamProvider>
          <SubscriptionProvider>
            <PreferencesProvider>
              <AnalyticsCacheProvider>
                  <TitleUpdater />
                  <FaviconUpdater />
                  <OnboardingRedirector />
                <Routes>
              {/* Redirect root to login */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              
              {/* Public routes */}
              <Route path="/login" element={<Interface />} />
              <Route path="/signup" element={<Interface />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/dashboard/public/:token" element={<PublicDashboardPage />} />
              <Route path="/analytics/public/:token" element={<PublicBoardPage />} />
              
              {/* Protected routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardToHomeRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/overview" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="overview">
                    <OverviewWrapper />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/overview/:subpage" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="overview">
                    <OverviewWrapper />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/custom-dashboard" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="custom-dashboard">
                    <CustomDashboardWrapper />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/custom-dashboard/dashboard" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="custom-dashboard">
                    <CustomDashboardWrapper />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/custom-dashboard/queries" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="custom-dashboard">
                    <CustomDashboardWrapper />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/offchain" element={
                <ProtectedRoute>
                  <OffchainRedirect />
                </ProtectedRoute>
              } />
              {/* Onchain route removed - onchain functionality removed */}
              <Route path="/:team/custom-events" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="custom-events">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/campaigns" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="campaigns">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/activity" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/activity/users/:distinctId" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/activity/groups/:groupKey/:groupId" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/activity/:subpage" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/events" element={<LegacyEventsRedirect />} />

              {/* User Profiles page disabled */}
              {/* <Route path="/:team/user-journey" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="history">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } /> */}
              <Route path="/:team/managewebsites" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="managewebsites">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/add-smart-contract" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/integrations" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/cq-intelligence" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="cq-intelligence">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/cipher" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Protected settings routes - all render Dashboard which shows Settings with secondary nav */}
              <Route path="/:team/settings" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/settings/usage" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/settings/members" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/settings/config" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/settings/notifications" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/settings/billing" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/settings/plans" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/settings/teamsSection" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/settings/pricing" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/user-settings" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/onboarding" element={
                <ProtectedRoute>
                  <TeamOnboarding />
                </ProtectedRoute>
              } />
              {/* Analytics (Mixpanel-style) */}
              <Route path="/:team/analytics" element={
                <ProtectedRoute>
                  <AnalyticsLayout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="reports" replace />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="boards" element={<Navigate to="reports" replace />} />
                <Route path="boards/:boardId" element={<BoardViewPage />} />
                <Route path="boards/:boardId/editor" element={<InsightsEditorPage />} />
                <Route path="flow" element={<FlowEditorPage />} />
                <Route path="flow/:boardId" element={<FlowEditorPage />} />
              </Route>

                </Routes>
                  </AnalyticsCacheProvider>
            </PreferencesProvider>
              </SubscriptionProvider>
            </TeamProvider>
          </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
